import _ from "lodash"
import { useCallback, useEffect } from "react"
import { Linking, Platform } from "react-native"

import { logger, resolveDynalink } from "../../capsule/utils"
import { dynamicLinkUrl, promsUrl, webAppUrl } from "../../common/types"
import { LoadFormParams } from "../Providers/PromsProvider"

const useDynalinks = (
  loadForm: ((params?: LoadFormParams) => Promise<boolean>) | undefined,
  setIsFromLink: (fromLink: boolean) => void,
  setWaitingInitialLink: (waiting: boolean) => void,
  setIsDynalinkLoading: (loading: boolean) => void,
) => {
  const handleDynamicLink = useCallback(
    async (link: { url: string }) => {
      try {
        const dynalinkUrl = link.url
        let resolvedUrl: string | undefined = dynalinkUrl
        if (Platform.OS !== "web") {
          resolvedUrl = await resolveDynalink(dynalinkUrl)
        }
        if (resolvedUrl && _.startsWith(resolvedUrl, webAppUrl)) {
          const data = _.split(resolvedUrl, "/")
          const formId = data[data.length - 2]
          const triggerDay = parseInt(data[data.length - 4])
          const token = _.replace(_.last(data) ?? "", "?auth=", "")
          setIsFromLink(true)
          setIsDynalinkLoading(false)
          loadForm?.({ id: formId, token, triggerDay })
        }
      } catch (error) {
        setIsDynalinkLoading(false)
        logger("Dynalink resolution failed:", error)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const handleWebUrl = useCallback(
    (url: string | null) => {
      if (url && _.includes(url, "FOR")) {
        handleDynamicLink({ url })
        setIsFromLink(true)
      }
    },
    [handleDynamicLink, setIsFromLink],
  )

  const launchDynamicLink = useCallback(() => {
    const subscription = Linking.addEventListener("url", ({ url }) => {
      if (url && _.startsWith(url, promsUrl) && url !== dynamicLinkUrl) {
        setIsDynalinkLoading(true)
        handleDynamicLink({ url })
      }
    })

    Linking.getInitialURL().then(url => {
      setWaitingInitialLink(false)
      if (url && _.startsWith(url, promsUrl) && url !== dynamicLinkUrl) {
        setIsDynalinkLoading(true)
        handleDynamicLink({ url })
      }
    })

    return () => subscription.remove()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (Platform.OS !== "web") {
      const unsubscribe = launchDynamicLink()
      return () => {
        unsubscribe?.()
      }
    } else {
      Linking.getInitialURL()
        .then(handleWebUrl)
        .catch(e => logger("Web URL error", e))
    }

    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

export default useDynalinks
